import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Pagination, Stack, Switch } from "@mui/material";
import moment from "moment";
import { Loader } from "../loader";
import api from "../../../api";
import { Plus, Trash2, Calendar } from "lucide-react";
import { useAlert } from "react-alert";

export function NoticeAdmin() {
  const alert = useAlert();
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loader, setLoader] = useState(true);
  const [show, setShow] = useState(false);

  const getNotices = async () => {
    try {
      setLoader(true);
      const res = await api.noticeGet({ page });
      setData(res?.notices || []);
      setTotal(res?.total || 0);
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getNotices();
  }, [page]);

  const deleteNotice = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) return;
    try {
      setLoader(true);
      await api.noticeDelete({ id });
      getNotices();
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(false);
    }
  };

  const toggleNotice = async (e, id) => {
    const isActive = e.target.checked;
    if (
      !window.confirm(
        `Are you sure you want to ${isActive ? "activate" : "deactivate"} this notice?`,
      )
    )
      return;
    try {
      setLoader(true);
      const res = await api.noticeToggle({ id, isActive });
      if (res?.success) {
        alert.show(res?.message || "Updated");
        getNotices();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="crypto-trades-admin">
      <div className="crypto-trades-header">
        <div className="content-of-header mb-0">
          <div className="title-section">
            <h1 className="crypto-trades-title">Mobile Notice Modal</h1>
          </div>
          <button
            className="crypto-trades-add-btn"
            onClick={() => setShow(true)}
          >
            <Plus size={18} />
            Add Notice
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="table-section">
          <div className="section-header">
            <h3>All Notices</h3>
            <div className="results-info">
              {total} {total === 1 ? "item" : "items"} found
            </div>
          </div>

          {data.length === 0 && !loader ? (
            <div className="empty-state">
              <div className="empty-icon">📢</div>
              <h3>No Notices Found</h3>
              <p>Start by adding your first notice</p>
              <button
                className="empty-action-btn"
                onClick={() => setShow(true)}
              >
                <Plus size={16} /> Add First Notice
              </button>
            </div>
          ) : (
            <>
              <div className="table-responsive-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>DESCRIPTION</th>
                      <th>TYPE</th>
                      <th>BUTTON TEXT</th>
                      <th>REDIRECT LINK</th>
                      <th>ACTIVE</th>
                      <th>CREATED AT</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item) => (
                      <tr key={item._id}>
                        <td style={{ maxWidth: 250, whiteSpace: "pre-wrap" }}>
                          {item.description}
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: 4,
                              background:
                                item.type === "app_update"
                                  ? "#fef3c7"
                                  : "#e0f2fe",
                              color:
                                item.type === "app_update"
                                  ? "#92400e"
                                  : "#0369a1",
                            }}
                          >
                            {item.type === "app_update"
                              ? "App Update"
                              : "Notice"}
                          </span>
                        </td>
                        <td>{item.buttonText}</td>
                        <td style={{ maxWidth: 180, wordBreak: "break-all" }}>
                          {item.redirectLink || "-"}
                        </td>
                        <td>
                          <Switch
                            checked={item.isActive}
                            onChange={(e) => toggleNotice(e, item._id)}
                          />
                        </td>
                        <td className="date-cell">
                          <div className="date-info">
                            <Calendar size={14} />
                            <span>
                              {moment(item.createdAt).format(
                                "MMM D, YYYY • h:mm A",
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="action-btn delete-btn"
                              onClick={() => deleteNotice(item._id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > 10 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {Math.min((page - 1) * 10 + 1, total)} to{" "}
                    {Math.min(page * 10, total)} of {total} entries
                  </div>
                  <Stack spacing={2}>
                    <Pagination
                      count={Math.ceil(total / 10)}
                      page={page}
                      onChange={(_, v) => setPage(v)}
                      shape="rounded"
                      variant="outlined"
                      size="medium"
                      className="custom-pagination"
                    />
                  </Stack>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        show={show}
        size="lg"
        onHide={() => setShow(false)}
        centered
        className="modern-modal"
      >
        <Modal.Body>
          <div className="modal-content-wrapper">
            <div className="modal-header">
              <h3>Add New Notice</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShow(false)}
              >
                ×
              </button>
            </div>
            <AddNoticeForm getNotices={getNotices} setShow={setShow} />
          </div>
        </Modal.Body>
      </Modal>

      {loader && <Loader />}
    </div>
  );
}

const APP_SCREENS = [
  "General",
  "Education",
  "Godzilla",
  "Updates",
  "Notification",
  "Spot",
  "MarketOverview",
  "Voice",
  "Live",
];

function AddNoticeForm({ getNotices, setShow }) {
  const alert = useAlert();
  const [form, setForm] = useState({
    type: "notice",
    description: "",
    buttonText: "",
    redirectLink: "",
    androidLink: "",
    iosLink: "",
  });
  const [redirectType, setRedirectType] = useState("external"); // "external" | "screen"
  const [loader, setLoader] = useState(false);
  const isUpdate = form.type === "app_update";

  const handleRedirectTypeChange = (val) => {
    setRedirectType(val);
    setForm({ ...form, redirectLink: "" });
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) return alert.show("Description is required");
    if (!form.buttonText.trim()) return alert.show("Button text is required");
    if (isUpdate && !form.androidLink.trim())
      return alert.show("Android store link is required");
    if (isUpdate && !form.iosLink.trim())
      return alert.show("iOS store link is required");
    try {
      setLoader(true);
      const res = await api.noticeCreate(form);
      if (res?.success) {
        alert.show("Notice created");
        getNotices();
        setShow(false);
      } else {
        alert.show(res?.message || "Something went wrong");
      }
    } catch (e) {
      alert.show("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="p-3">
      {/* Type selector */}
      <div className="mb-3">
        <label className="form-label fw-500">Notice Type *</label>
        <div className="d-flex gap-3">
          {[
            { value: "notice", label: "General Notice" },
            { value: "app_update", label: "App Update" },
          ].map((opt) => (
            <div
              key={opt.value}
              className="form-check"
              style={{ cursor: "pointer" }}
              onClick={() => setForm({ ...form, type: opt.value })}
            >
              <input
                className="form-check-input"
                type="radio"
                readOnly
                checked={form.type === opt.value}
              />
              <label className="form-check-label" style={{ cursor: "pointer" }}>
                {opt.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label fw-500">Description *</label>
        <textarea
          className="form-control"
          rows={4}
          placeholder="Enter notice message..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-500">Button Text *</label>
        <input
          type="text"
          className="form-control"
          placeholder={isUpdate ? "e.g. Update Now" : "e.g. Learn More"}
          value={form.buttonText}
          onChange={(e) => setForm({ ...form, buttonText: e.target.value })}
        />
      </div>

      {isUpdate ? (
        <>
          <div className="mb-3">
            <label className="form-label fw-500">Android Store Link *</label>
            <input
              type="text"
              className="form-control"
              placeholder="https://play.google.com/store/apps/details?id=..."
              value={form.androidLink}
              onChange={(e) =>
                setForm({ ...form, androidLink: e.target.value })
              }
            />
          </div>
          <div className="mb-4">
            <label className="form-label fw-500">iOS Store Link *</label>
            <input
              type="text"
              className="form-control"
              placeholder="https://apps.apple.com/us/app/..."
              value={form.iosLink}
              onChange={(e) => setForm({ ...form, iosLink: e.target.value })}
            />
          </div>
        </>
      ) : (
        <div className="mb-4">
          <label className="form-label fw-500">Redirect</label>

          {/* Toggle: External Link / Screen */}
          <div className="d-flex gap-2 mb-2">
            {[
              { value: "external", label: "External Link" },
              { value: "screen", label: "In-App Screen" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleRedirectTypeChange(opt.value)}
                style={{
                  padding: "5px 14px",
                  borderRadius: 6,
                  border: "1px solid",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  borderColor:
                    redirectType === opt.value ? "#0d6efd" : "#ced4da",
                  background:
                    redirectType === opt.value ? "#0d6efd" : "transparent",
                  color: redirectType === opt.value ? "#fff" : "#6c757d",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {redirectType === "screen" ? (
            <select
              className="form-control"
              value={form.redirectLink}
              onChange={(e) =>
                setForm({ ...form, redirectLink: e.target.value })
              }
            >
              <option value="">— Select a screen —</option>
              {APP_SCREENS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              className="form-control"
              placeholder="https://..."
              value={form.redirectLink}
              onChange={(e) =>
                setForm({ ...form, redirectLink: e.target.value })
              }
            />
          )}
        </div>
      )}

      <button
        className="themeBtn"
        style={{ display: "block", margin: "0 auto", minWidth: 180 }}
        onClick={handleSubmit}
        disabled={loader}
      >
        {loader && <i className="fa fa-spinner fa-pulse me-2" />}
        Create Notice
      </button>
    </div>
  );
}
